// client\src\pages\MyLearning\Completed.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getCompletedCourses } from '../../services/myLearningService';
import { useNotification } from '../../context/NotificationContext';
import { formatDate } from '../../utils/dateUtils';

function Completed() {
    const { showNotification } = useNotification();
    const [completedCourses, setCompletedCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        const fetchCompletedCourses = async () => {
            try {
                const response = await getCompletedCourses();
                if (response.success) {
                    setCompletedCourses(response.data || []);
                } else {
                    setError(response.message || "Failed to load completed courses.");
                }
            } catch (err) {
                setError("An error occurred while fetching completed courses.");
            } finally {
                setLoading(false);
            }
        };
        fetchCompletedCourses();
    }, []);

    const handleMenuOpen = (event, courseId) => {
        setAnchorEl(event.currentTarget);
        setSelectedCourse(courseId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCourse(null);
    };

    const handleRetakeCourse = (courseId) => {
        console.log(`Retake course with ID: ${courseId}`);
        showNotification("Course has been reset for retake", "info");
        handleMenuClose();
    };

    const handleCopyCourseURL = (course) => {
        navigator.clipboard.writeText(`${window.location.origin}/courses/${course.course.slug}`)
            .then(() => showNotification("Course link has been copied", "info"))
            .catch((err) => console.error("Failed to copy link", err));
        handleMenuClose();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (completedCourses.length === 0) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography variant="h6">No Completed Courses</Typography>
                <Typography variant="body2" color="textSecondary">
                    Once you complete a course, it will be listed here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {completedCourses.map((course) => (
                <Card
                    key={course._id}
                    sx={{
                        marginBottom: '1rem',
                        padding: '0.4rem',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                        },
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    <CardContent>
                        <IconButton
                            onClick={(e) => handleMenuOpen(e, course._id)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                            }}
                        >
                            <MoreVertIcon />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={open && selectedCourse === course._id}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem onClick={() => handleRetakeCourse(course._id)}>Retake Course</MenuItem>
                            <MenuItem onClick={() => handleCopyCourseURL(course)}>Copy Course URL</MenuItem>
                        </Menu>

                        {/* Domain Chip */}
                        <Chip
                            label={course.course.domain}
                            color="primary"
                            size="small"
                            sx={{
                                marginBottom: '0.7rem',
                                fontWeight: 'bold',
                                padding: '0 8px',
                                borderRadius: '4px',
                            }}
                        />

                        {/* Course Title */}
                        <Typography
                            variant="h6"
                            sx={{
                                marginBottom: '0.5rem',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {course.course.title}
                        </Typography>

                        {/* Completion Date */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Completed on: {formatDate(course.completedAt)}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}

export default Completed;
